import { ArrowLeftIcon } from "@heroicons/react/solid";
import { Base64 } from "js-base64";
import {
  find,
  flatten,
  head,
  isEmpty,
  map,
  orderBy,
  reduce,
  sumBy,
  values
} from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import CommentsComponent from "../../../components/comments";
import { getLabelColorStyle } from "../../../components/labels/utils";
import { useAppearance } from "../../../context/appearance";
import { useCommissionReports } from "../../../context/commissionReports";
import { getCommissionReportTitle } from "../../../context/commissionReports/CommissionReportsProvider";
import { PAYMENT_MEMO_MAX_CHARACTERS } from "../../../context/commissionReports/HntPaymentSidebar";
import { useContacts } from "../../../context/contacts";
import {
  LoadingKey,
  useLoading
} from "../../../context/loading/LoadingProvider";
import { usePaymentTransactions } from "../../../context/paymentTransactions";
import { useWallets } from "../../../context/wallets";
import {
  CommissionPaymentStatus,
  ContactCommissions,
  DataById,
  PaymentTransaction
} from "../../../utils/types";
import useCurrency, { CurrencyKey } from "../../../utils/useCurrency";
import { useHntMarket } from "../../../utils/useHntMarket";
import { PAYMENT_TRANSACTIONS_MAX_WALLETS_TO_FETCH } from "../common/PaymentTransactionsDataTable";
import CommissionReportActionsButton from "./CommissionReportActionsButton";
import CommissionReportContacts from "./CommissionReportContacts";
import { getCommissionReportPeriodText } from "./CommissionReports";

interface Props extends RouteComponentProps<{ commissionReportId: string }> {}

const CommissionReportDetails: React.FC<Props> = ({ match }) => {
  const { getIsReady } = useLoading();
  const { isDark } = useAppearance();
  const { wallets } = useWallets();
  const { getDefaultPaymentMethod } = useContacts();
  const { getCurrencyTitle, currencyDisplay } = useCurrency();
  const { commissionReports, currentCommissionReport: commissionReport } =
    useCommissionReports();
  const { displayInCurrency } = useHntMarket();
  const { paymentTransactionsByWallet, fetchPaymentTransactions } =
    usePaymentTransactions();
  const [fetchedTransactions, setFetchedTransactions] = useState(false);

  const workspaceWalletAddressesToFetch = useMemo(
    () =>
      map(values(wallets), "address").slice(
        0,
        PAYMENT_TRANSACTIONS_MAX_WALLETS_TO_FETCH
      ),
    [wallets]
  );

  const isLoadingTransactions = useMemo(
    () =>
      !getIsReady([LoadingKey.workspaceWallets]) ||
      (!isEmpty(workspaceWalletAddressesToFetch) &&
        !getIsReady([LoadingKey.paymentTransactions])),
    [getIsReady, workspaceWalletAddressesToFetch]
  );

  useEffect(() => {
    if (!isLoadingTransactions && !fetchedTransactions) {
      fetchPaymentTransactions(workspaceWalletAddressesToFetch);
      setFetchedTransactions(true);
    }
  }, [
    fetchPaymentTransactions,
    fetchedTransactions,
    isLoadingTransactions,
    workspaceWalletAddressesToFetch
  ]);

  const isLoading = useMemo(
    () =>
      isEmpty(commissionReports) &&
      !getIsReady([LoadingKey.workspaceCommissionReports]),
    [commissionReports, getIsReady]
  );

  const encodedPaymentMemo = useMemo(() => {
    if (!commissionReport?.uuid) return "";

    const memo = commissionReport.uuid.substr(-PAYMENT_MEMO_MAX_CHARACTERS);
    return Base64.encode(memo);
  }, [commissionReport]);

  const contactCommissionTransactions: DataById<PaymentTransaction[]> =
    useMemo(() => {
      if (!commissionReport || commissionReport.currency !== CurrencyKey.hnt) {
        return {};
      }

      const transactionsSinceReport = orderBy(
        flatten(values(paymentTransactionsByWallet)).filter(
          ({ block, status }) =>
            status !== "cleared" ||
            block > (commissionReport.current_block as number)
        ),
        "time",
        "desc"
      );

      return reduce(
        commissionReport.report,
        (
          acc: DataById<PaymentTransaction[]>,
          { contact, totalCommissionValue }
        ) => {
          const defaultPaymentMethod = getDefaultPaymentMethod(
            contact.uuid,
            commissionReport.currency
          );
          const walletAddress = JSON.parse(
            defaultPaymentMethod?.metadata || "{}"
          )?.walletAddress;

          if (!walletAddress) return acc;

          const contactTransactions: PaymentTransaction[] = [];

          transactionsSinceReport.forEach((tx) => {
            if (
              find(
                tx.payments,
                (payment) =>
                  payment.payee === walletAddress &&
                  payment.memo === encodedPaymentMemo &&
                  Math.abs(payment.amount - Math.floor(totalCommissionValue)) <=
                    1
              )
            ) {
              contactTransactions.push(tx);
            }
          });

          acc[contact.uuid] = contactTransactions;

          return acc;
        },
        {}
      );
    }, [
      commissionReport,
      paymentTransactionsByWallet,
      getDefaultPaymentMethod,
      encodedPaymentMemo
    ]);

  const getPaymentStatus = useCallback(
    (contactCommissions: ContactCommissions): CommissionPaymentStatus => {
      if (!commissionReport) return { status: "loading" };

      const contactTransactions =
        contactCommissionTransactions[contactCommissions.contact.uuid];

      const paymentTransaction = head(contactTransactions);

      const contactPayment = find(commissionReport.contact_payments, {
        contact_uuid: contactCommissions.contact.uuid
      });

      if (contactPayment) {
        return { contactPayment, paymentTransaction, status: "paid" };
      }

      if (!paymentTransaction && isLoadingTransactions) {
        return { paymentTransaction, status: "loading" };
      }

      switch (paymentTransaction?.status) {
        case "failed":
          return { paymentTransaction, status: "failed" };
        case "cleared":
          return { paymentTransaction, status: "paid" };
        default:
          return { paymentTransaction, status: "unpaid" };
      }
    },
    [commissionReport, contactCommissionTransactions, isLoadingTransactions]
  );

  const contactsWithoutDefaultPaymentMethod = useMemo(() => {
    if (!commissionReport) return 0;

    return commissionReport?.report
      ? commissionReport?.report?.filter(
          ({ contact }) =>
            !getDefaultPaymentMethod(contact.uuid, commissionReport.currency)
        ).length
      : 0;
  }, [commissionReport, getDefaultPaymentMethod]);

  const [paidCommissionsAmount, paidCommissionsCount] = useMemo(() => {
    if (!commissionReport) return [0, 0];

    let paidContactCommissions: ContactCommissions[] = [];

    if (commissionReport?.report) {
      paidContactCommissions = commissionReport?.report?.filter(
        (contactCommissions) =>
          getPaymentStatus(contactCommissions).status === "paid"
      );
    }

    return [
      sumBy(paidContactCommissions, "totalCommissionValue"),
      paidContactCommissions.length
    ];
  }, [commissionReport, getPaymentStatus]);

  const renderCommissionReportContacts = useMemo(
    () => (
      <CommissionReportContacts
        commissionReport={commissionReport}
        getPaymentStatus={getPaymentStatus}
      />
    ),
    [commissionReport, getPaymentStatus]
  );

  if (isLoading) return <CenteredLoading className="h-96" />;

  if (!commissionReport) return null;

  return (
    <div className="dark:text-white overflow-y-clip">
      <div className="sm:my-4 sm:mx-4 my-4 mx-4">
        <Link
          className="text-sm flex items-center hover:text-gray-600 truncate font-semibold dark:hover:text-white dark:hover:underline"
          to="/workspace/commission-reports"
        >
          <ArrowLeftIcon className="h-3 w-3 inline mr-1" />
          Return to list
        </Link>

        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl dark:text-gray-50 flex items-center justify-between">
          <span>{getCommissionReportTitle(commissionReport)}</span>
          <CommissionReportActionsButton commissionReport={commissionReport} />
        </h1>
        <div className="flex items-center flex-wrap">
          <div
            className="inline-flex items-center rounded-full font-medium px-3 text-sm mr-2 mt-2"
            style={getLabelColorStyle("#7057ff", isDark)}
          >
            {getCommissionReportPeriodText(commissionReport)}
          </div>
          <div
            className="inline-flex items-center rounded-full font-medium px-3 text-sm mt-2"
            style={getLabelColorStyle("#d73aaa", isDark)}
          >
            {getCurrencyTitle(commissionReport.currency)}
          </div>
        </div>

        <div className="mt-6">
          <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                  Commissions
                </dt>

                <div className="flex items-baseline">
                  <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                    {commissionReport.currency === CurrencyKey.hnt
                      ? displayInCurrency(
                          commissionReport.total_commission_amount,
                          {
                            withHntSuffix: true,
                            needsDivision: true
                          }
                        )
                      : currencyDisplay(
                          commissionReport.currency,
                          commissionReport.total_commission_amount
                        )}
                  </dd>

                  <div className="ml-2 flex items-baseline text-sm text-gray-500 dark:text-gray-400">
                    {commissionReport.currency === CurrencyKey.hnt
                      ? displayInCurrency(paidCommissionsAmount, {
                          withHntSuffix: true,
                          needsDivision: true
                        })
                      : currencyDisplay(
                          commissionReport.currency,
                          paidCommissionsAmount
                        )}{" "}
                    paid,{" "}
                    {commissionReport.currency === CurrencyKey.hnt
                      ? displayInCurrency(
                          commissionReport.total_commission_amount -
                            paidCommissionsAmount,
                          {
                            withHntSuffix: true,
                            needsDivision: true
                          }
                        )
                      : currencyDisplay(
                          commissionReport.currency,
                          commissionReport.total_commission_amount -
                            paidCommissionsAmount
                        )}{" "}
                    unpaid
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                  Contacts with {getCurrencyTitle(commissionReport.currency)}{" "}
                  commissions
                </dt>

                <div className="flex items-baseline">
                  <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                    {commissionReport?.report?.length ?? (
                      <span className="flex w-5 h-[26px] animate-pulse bg-gray-400 rounded mt-1"></span>
                    )}
                  </dd>

                  <div className="ml-2 flex items-baseline text-sm text-gray-500 dark:text-gray-400">
                    {paidCommissionsCount} paid,{" "}
                    {(commissionReport?.report?.length ?? 0) -
                      paidCommissionsCount}{" "}
                    unpaid
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                  Contacts without {getCurrencyTitle(commissionReport.currency)}{" "}
                  payment method
                </dt>

                <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                  {contactsWithoutDefaultPaymentMethod}
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </div>

      {renderCommissionReportContacts}

      <div className="max-w-2xl px-4 sm:px-6 lg:px-8 mb-6">
        <div className="mt-6 pb-5">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
            Comments
          </h3>
          <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
            Use the comments section to save text information relevant to this
            commission report. Tagging other workspace members using the{" "}
            <b>@ sign</b> emails them the comment.
          </p>
        </div>

        <CommentsComponent
          type="commission-report"
          typeUuid={commissionReport.uuid}
        />
      </div>
    </div>
  );
};

export default CommissionReportDetails;
